import { CheckCircle2, PlugZap } from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { Badge } from "../../../client/components/ui/badge";
import useTranslation from "../../../client/hooks/useTranslation";
import type { BrokerHealthRow } from "../../../analytics/adminBrokerHealth";

interface Props {
  rows: BrokerHealthRow[];
  totalConnections: number;
  onSelectUser?: (userId: string) => void;
}

const BrokerHealthCard = ({ rows, totalConnections, onSelectUser }: Props) => {
  const t = useTranslation();

  const statusLabel = (status: string): string => {
    switch (status) {
      case "error":
        return t.adminDashboard.statusError;
      case "needs_reauth":
        return t.adminDashboard.statusNeedsReauth;
      case "foreign_token":
        return t.adminDashboard.statusForeignToken;
      case "delayed":
        return t.adminDashboard.statusDelayed;
      case "deprovisioned":
        return t.adminDashboard.statusDeprovisioned;
      default:
        return status;
    }
  };

  // "delayed" est une degradation transitoire, pas encore une panne franche.
  const statusVariant = (status: string) =>
    status === "delayed" ? "outline" : "destructive";

  return (
    <div>
      <h2 className="text-foreground mb-1 text-lg font-semibold">
        {t.adminDashboard.brokerHealthTitle}
        {rows.length > 0 && (
          <span className="text-muted-foreground ml-2 font-mono text-sm">
            {rows.length}/{totalConnections}
          </span>
        )}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.brokerHealthHint}
      </p>

      <Card>
        <CardContent className="pt-4">
          {rows.length === 0 ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-xs">
              <CheckCircle2 className="size-4 text-emerald-500" />
              {t.adminDashboard.brokerHealthEmpty}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-left text-xs">
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.brokerProvider}
                    </th>
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.brokerUser}
                    </th>
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.brokerStatus}
                    </th>
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.brokerLastSync}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t.adminDashboard.brokerAccounts}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={`${row.provider}-${row.connectionId}`}
                      className="border-border/60 border-b last:border-b-0"
                    >
                      <td className="py-2.5">
                        <span className="text-muted-foreground flex items-center gap-1.5 text-xs capitalize">
                          <PlugZap className="size-3.5" />
                          {row.provider}
                        </span>
                      </td>
                      <td className="py-2.5">
                        <button
                          type="button"
                          onClick={() => onSelectUser?.(row.userId)}
                          className="text-foreground hover:text-primary max-w-[16rem] truncate text-left underline-offset-2 hover:underline"
                        >
                          {row.userEmail ?? row.userId}
                        </button>
                        {row.lastSyncError && (
                          <p
                            className="text-muted-foreground max-w-[24rem] truncate text-[11px]"
                            title={row.lastSyncError}
                          >
                            {row.lastSyncError}
                          </p>
                        )}
                      </td>
                      <td className="py-2.5">
                        <Badge
                          variant={statusVariant(row.status)}
                          className="text-[10px]"
                        >
                          {statusLabel(row.status)}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground py-2.5 text-xs">
                        {row.lastSyncAt
                          ? new Date(row.lastSyncAt).toLocaleString()
                          : t.adminDashboard.brokerNeverSynced}
                      </td>
                      <td className="text-foreground py-2.5 text-right font-mono">
                        {row.accountCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BrokerHealthCard;
